import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Enforce a single, consistent browser tab title across the whole app.
    document.title = "SERP";
    (async () => {
      await refresh();
      setReady(true);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Session lives entirely in the HttpOnly access_token cookie set by the API.
    setUser(data);
    return data;
  };

  // Passkey login — same shape as password login, just a different auth path.
  const loginWithSession = (data) => { setUser(data); return data; };

  const register = async (email, password, name, company) => {
    // Registration now returns pending_approval — DO NOT auto-log-in.
    const { data } = await api.post("/auth/register", { email, password, name, company });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("ba_token"); // clean up any legacy value
    setUser(false);
  };

  const role = user?.role;
  const isSuperAdmin = role === "super_admin";
  const isAdmin = role === "super_admin" || role === "admin"; // admins-or-above
  const isUser = role === "user";

  return (
    <AuthCtx.Provider value={{
      user, ready, login, loginWithSession, register, logout, refresh,
      role, isSuperAdmin, isAdmin, isUser,
      formatApiErrorDetail,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
